import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useFetchJSON } from "@/db/dbManag";
import { API } from "@/enums/endpoint";
import type { MenuPage, MenuSection, MenuViewResponse } from "@/types/menu";
import { useComandiEnabled } from "./comandiEnabled";
import { msalInstance, silentTokenRequest } from "@/authConfig";

// FIX TEMPORANEO: alcune voci di menu arrivano dal BE senza `route`. Finché il
// flusso del menu non viene sistemato, ricaviamo la route dallo slug dell'heading
// (es. "Gare a Squadre" -> "/gare-a-squadre"). Le voci con sottomenu restano
// senza route (è corretto: sono contenitori, non pagine navigabili).
const slugify = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // rimuove i diacritici (es. à -> a)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-") // qualsiasi separatore -> trattino singolo
    .replace(/^-+|-+$/g, "");

const deriveRouteFromHeading = (page: MenuPage): MenuPage => {
  if (page.route || page.sub?.length) return page;
  const slug = slugify(page.heading ?? "");
  if (!slug) return page;
  return { ...page, route: `/${slug}` };
};

const sortPagesByOrder = (pages: MenuPage[]): MenuPage[] => {
  return [...pages]
    .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))
    .map(page => (page.sub?.length ? { ...page, sub: sortPagesByOrder(page.sub) } : deriveRouteFromHeading(page)));
};

const extractComandi = (payload: MenuViewResponse | null) => {
  if (!payload) return [];
  // Privilegiamo i comandi dichiarati dentro le sezioni (anche se array vuoto: vuol dire "nessun comando abilitato").
  const sectionDeclares = payload.menu?.some(s => s.comandi_enabled !== undefined) ?? false;
  if (sectionDeclares) return payload.menu.flatMap(s => s.comandi_enabled ?? []);
  return payload.comandi_enabled ?? [];
};

export const useMenuStore = defineStore("menuStore", () => {
  const sections = ref<MenuSection[]>([]);
  const pagineAbilitate = ref<string[]>([]);
  const loaded = ref(false);

  let loadPromise: Promise<void> | null = null;

  // Il token API (tokenAdb2c) in localStorage viene aggiornato dal flusso MSAL in ritardo: al primo
  // caricamento dopo il login resta quello dell'utente precedente finché non si ricarica la pagina.
  // Acquisiamo quindi il token dell'account ATTIVO direttamente da MSAL e lo riscriviamo prima della fetch.
  const ensureFreshToken = async (timeoutMs = 8000): Promise<boolean> => {
    const start = Date.now();
    let account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
    while (!account && Date.now() - start < timeoutMs) {
      await new Promise(resolve => setTimeout(resolve, 100));
      account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
    }
    if (!account) return false;
    try {
      const result = await msalInstance.acquireTokenSilent({ ...silentTokenRequest, account });
      localStorage.setItem("tokenAdb2c", result.idToken);
      return true;
    } catch {
      return false;
    }
  };

  const load = (): Promise<void> => {
    if (loadPromise) return loadPromise;
    loadPromise = (async () => {
      if (!(await ensureFreshToken())) {

        // Token non disponibile: non memorizziamo il fallimento così il prossimo tentativo riprova.
        loadPromise = null;
        return;
      }

      const { data, execute, error } = useFetchJSON(API.GET.VIEW.MENU).get().json();
      await execute();

      const payload = data.value as MenuViewResponse | null;

      if (error.value || !payload?.menu) {

        // Non memorizziamo un caricamento fallito: azzeriamo il Promise così il prossimo tentativo riprova.
        loadPromise = null;
        sections.value = [];
        pagineAbilitate.value = [];
        loaded.value = false;

        return;
      }

      sections.value = payload.menu.map(s => ({ ...s, pages: sortPagesByOrder(s.pages ?? []) }));
      pagineAbilitate.value = payload.pagine_abilitate ? [...payload.pagine_abilitate] : [];
      useComandiEnabled().setComandiEnabled(extractComandi(payload));

      loaded.value = true;
    })();
    return loadPromise;
  };

  const findFirstRoute = (pages: MenuPage[]): string | null => {
    for (const page of pages) {
      if (page.route) return page.route;
      if (page.sub?.length) {
        const nested = findFirstRoute(page.sub);
        if (nested) return nested;
      }
    }
    return null;
  };

  const firstAvailableRoute = computed<string | null>(() => {
    for (const section of sections.value) {
      const route = findFirstRoute(section.pages ?? []);
      if (route) return route;
    }
    return null;
  });

  const isEmpty = computed(() => firstAvailableRoute.value === null);

  const isRouteAuthorized = (path: string): boolean => {
    // Prima del caricamento, non blocchiamo: la guard awaita load() comunque.
    if (!loaded.value) return true;
    return pagineAbilitate.value.some(allowed => path === allowed || path.startsWith(allowed + "/"));
  };

  return {
    sections,
    pagineAbilitate,
    loaded,
    load,
    isEmpty,
    isRouteAuthorized,
    firstAvailableRoute,
  };
});
